import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Autocomplete,
  Icon,
} from "@medentic/polaris-react";
import { SearchIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import useTimezones from "@shared/hooks/common/useTimezones";
import { Timezone } from "@shared/types/commonTypes";

interface TimezoneAutoCompleteProps {
  /** Currently selected timezone - can be ID string, full Timezone object, or null */
  value: string | Timezone | null;

  /** Called when the user selects a timezone - returns full Timezone object */
  onChange: (timezone: Timezone | null) => void;

  /** Localized label for the field */
  label?: string;

  /** Localized placeholder text */
  placeholder?: string;

  /** Error message to display */
  error?: string;

  /** Show required indicator on the field */
  requiredIndicator?: boolean;

  /** Automatically select the best timezone based on browser's Intl API */
  autopick?: boolean;
}

export const TimezoneAutoComplete: React.FC<TimezoneAutoCompleteProps> = ({
  value,
  onChange,
  label,
  placeholder,
  error,
  requiredIndicator,
  autopick = false,
}) => {
  const { t } = useTranslation();
  const { timezones, isLoading } = useTimezones();

  // Provide default localizable text if none is passed via props
  const computedLabel = label ?? t("TimezoneAutoComplete.label");
  const computedPlaceholder = placeholder ?? t("TimezoneAutoComplete.placeholder");
  const noResultsText = t("TimezoneAutoComplete.noResults");

  // Extract timezone ID from value (handle both string and Timezone object)
  const timezoneId = useMemo(() => {
    if (!value) return null;
    return typeof value === 'string' ? value : value.id;
  }, [value]);

  const [query, setQuery] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);
  const hasAutoPickedRef = useRef(false);

  // Get browser timezone and attempt to match it with the timezone list
  const detectBrowserTimezone = useCallback((): string | null => {
    try {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Try to find exact match by ID
      const exactMatch = timezones.find(
        (tz) => tz.id.toLowerCase() === browserTz.toLowerCase()
      );
      
      if (exactMatch) {
        return exactMatch.id;
      }

      return null;
    } catch (error) {
      console.warn("Failed to detect browser timezone:", error);
      return null;
    }
  }, [timezones]);

  // Auto-pick timezone on mount if enabled and no value is set
  useEffect(() => {
    if (autopick && !timezoneId && timezones.length > 0 && !hasAutoPickedRef.current) {
      const detectedTimezoneId = detectBrowserTimezone();
      if (detectedTimezoneId) {
        const detectedTimezone = timezones.find((tz) => tz.id === detectedTimezoneId);
        if (detectedTimezone) {
          hasAutoPickedRef.current = true;
          onChange(detectedTimezone);
        }
      }
    }
  }, [autopick, timezoneId, timezones, detectBrowserTimezone, onChange]);

  const selectedLabel = useMemo(() => {
    if (!timezoneId || timezones.length === 0) return "";
    return timezones.find((tz) => tz.id === timezoneId)?.label ?? "";
  }, [timezoneId, timezones]);

  const inputValue = isFiltering ? query : selectedLabel;
  const filterValue = isFiltering ? query : "";

  const updateText = useCallback((newValue: string) => {
    setQuery(newValue);
    setIsFiltering(true);

    // If user clears the field, set value to null
    if (!newValue && timezoneId) {
      onChange(null);
    }
  }, [timezoneId, onChange]);

  const updateSelection = useCallback(
    (selected: string[]) => {
      const selectedTimezoneId = selected[0];
      const selectedTimezone = timezones.find((tz) => tz.id === selectedTimezoneId);

      if (selectedTimezone) {
        setIsFiltering(false);
        setQuery(selectedTimezone.label);
        onChange(selectedTimezone);
      }
    },
    [timezones, onChange]
  );

  // Filter options based on input - memoized to prevent recalculation
  const filteredOptions = useMemo(() => {
    if (!filterValue) {
      return timezones;
    }

    const filterRegex = new RegExp(filterValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
    return timezones.filter(
      (tz) =>
        tz.label?.match(filterRegex) ||
        tz.id?.match(filterRegex) ||
        tz.countries?.some((country) => country.match(filterRegex))
    );
  }, [filterValue, timezones]);

  // Generate options markup
  const optionsMarkup = useMemo(
    () =>
      filteredOptions.map((tz) => ({
        value: tz.id,
        label: tz.label,
      })),
    [filteredOptions]
  );

  const textField = useMemo(
    () => (
      <Autocomplete.TextField
        onChange={updateText}
        label={computedLabel}
        value={inputValue}
        placeholder={
          isLoading 
            ? t("TimezoneAutoComplete.loading") 
            : computedPlaceholder
        }
        autoComplete="off"
        prefix={<Icon source={SearchIcon} />}
        error={error}
        requiredIndicator={requiredIndicator}
        disabled={isLoading}
      />
    ),
    [updateText, computedLabel, inputValue, computedPlaceholder, error, requiredIndicator, isLoading, t]
  );

  return (
    <Autocomplete
      options={optionsMarkup}
      selected={timezoneId ? [timezoneId] : []}
      onSelect={updateSelection}
      textField={textField}
      emptyState={noResultsText}
      loading={isLoading}
    />
  );
};
